import type { ReactNode } from "react";
import Typography from "@mui/material/Typography";
import { BEAR_TINT, BULL_TINT, textToneClass } from "./format";
import type { SignalTone } from "./SignalBadge";

// Shared chrome for the trade/spread cards: header bar, tone pills, divider, rows.
const SIGNAL_CLASS: Record<SignalTone, string> = {
  bull: `${BULL_TINT} text-success-main`,
  bear: `${BEAR_TINT} text-error-main`,
  neutral: "bg-mui-black-08 text-token-text-secondary dark:bg-mui-white-12",
};
const SIGNAL_DOT: Record<SignalTone, string> = {
  bull: "bg-success-main",
  bear: "bg-error-main",
  neutral: "bg-token-text-secondary",
};

const PILL_BASE
  = "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-normal uppercase leading-[1.38] tracking-[0.16px]";

export const SignalPill = ({ label, tone }: { label: string; tone: SignalTone }) => (
  <span className={`${PILL_BASE} ${SIGNAL_CLASS[tone]}`}>
    <span className={`h-1.5 w-1.5 rounded-full ${SIGNAL_DOT[tone]}`} />
    {label}
  </span>
);

export const ExpiryPill = ({ label }: { label: string }) => (
  <span className={`${PILL_BASE} bg-mui-black-08 text-token-text-secondary dark:bg-mui-white-12`}>{label}</span>
);

export const Divider = () => <div className="border-t border-mui-black-12 dark:border-mui-white-12" />;

export const CardHeaderBar = ({ left, ticker, right }: { left?: ReactNode; ticker: string; right?: ReactNode }) => (
  <section className="flex items-center justify-between gap-2">
    <div className="flex flex-1 items-center gap-2">{left}</div>
    <Typography variant="body1/regular-600">{ticker}</Typography>
    <div className="flex flex-1 items-center justify-end">{right}</div>
  </section>
);

export type HeadlineTone = "primary" | "success" | "error";
export type HeadlineStat = { label: string; value: string; tone: HeadlineTone };

export const HeadlineStats = ({ items }: { items: HeadlineStat[] }) => (
  <section className="flex items-start justify-between">
    {items.map((it, i) => (
      <div
        key={it.label}
        className={`flex flex-col gap-1 ${i === 0 ? "items-start" : i === items.length - 1 ? "items-end" : "items-center"}`}
      >
        <span className="text-xs uppercase tracking-wide text-token-text-secondary">{it.label}</span>
        <span className={`text-xl font-bold ${textToneClass(it.tone)}`}>{it.value}</span>
      </div>
    ))}
  </section>
);

export type DetailRow = { label: string; value: string; tone: HeadlineTone };

export const DetailRows = ({ rows }: { rows: DetailRow[] }) => (
  <section className="flex flex-col gap-2 text-sm">
    {rows.map(r => (
      <div key={r.label} className="flex items-center justify-between gap-4">
        <span className="text-token-text-secondary">{r.label}</span>
        <span className={textToneClass(r.tone)}>{r.value}</span>
      </div>
    ))}
  </section>
);
